import React from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(400px);
  }
  to {
    transform: translateX(0);
  }
`;

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100%;
  background: #0B1122;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  z-index: 300;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease;
`;

const SidebarTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.25rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  option {
    background: #0B1122;
    color: white;
  }
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ImagePreview = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 0.25rem;
`;

const DocumentList = styled.ul`
  font-size: 0.75rem;
  color: #5ce9ff;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  color: black;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const StoreEditor = ({ store, isOpen, onClose, onSave, canPublish }) => {
  if (!isOpen || !store) return null;

  return (
    <SidebarContainer>
        <SidebarTitle>Store Editor</SidebarTitle>
        
        <FormGroup>
          <Label>Store name</Label>
          <Input
            type="text"
            placeholder="Store name"
            value={store.name || ''}
            readOnly
          />
        </FormGroup>

        <FormGroup>
          <Label>Slug</Label>
          <Input
            type="text"
            placeholder="Slug"
            value={store.slug || ''}
            readOnly
          />
        </FormGroup>

        <FormGroup>
          <Label>Short description</Label>
          <TextArea
            placeholder="Short description"
            value={store.shortDescription || ''}
            readOnly
            rows={3}
          />
        </FormGroup>

        <FormGroup>
          <Label>Store Images</Label>
          <ImageGrid>
            {store.images?.map((img, i) => (
              <ImagePreview
                key={i}
                src={img}
                alt={`Store ${i + 1}`}
              />
            ))}
          </ImageGrid>
        </FormGroup>

        <FormGroup>
          <Label>Traceability Documents</Label>
          <DocumentList>
            {store.documents?.map((d, i) => (
              <li key={i}>{d.name}</li>
            ))}
          </DocumentList>
        </FormGroup>

        <FormGroup>
          <Label>Role</Label>
          <Select
            value={store.role || 'standard'}
            disabled
          >
            <option value="standard">Standard</option>
            <option value="enterprise">Enterprise</option>
            <option value="pharma">Pharma</option>
          </Select>
        </FormGroup>

        {canPublish && (
          <FormGroup>
            <Label>Status</Label>
            <Select
              value={store.status || 'draft'}
              disabled
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Select>
          </FormGroup>
        )}

        <ButtonGroup>
          <SaveButton onClick={onSave}>
            Save
          </SaveButton>
          <CancelButton onClick={onClose}>
            Cancel
          </CancelButton>
        </ButtonGroup>
      </SidebarContainer>
  );
};

export default StoreEditor;
